/* Prox Waitlist — form + counter + position + referral logic.
 *
 * Submission: posts to Shopify's built-in /contact endpoint with form_type=customer
 * when no backend is configured. With a backend URL set, posts to {backend}/join
 * which handles customer creation, code assignment, referral attribution, and
 * sequential position assignment server-side.
 *
 * Referral program:
 *   - Each new signup gets a unique referral code (PROX-XXXXXX).
 *   - URL ?ref=CODE captures attribution (also persisted in sessionStorage).
 *   - Three reward tiers (configurable in theme editor): default 2 friends = free
 *     can, 5 = free 4-pack, 10 = free 12-pack.
 *   - With backend: real counts via /me?code=CODE, polled while the page is open.
 *   - Without backend: code generated locally; counts stay at 0 (visual demo).
 */
(() => {
  const root = document.querySelector('.prox-waitlist');
  if (!root) return;

  const form    = root.querySelector('form.waitlist-form');
  const input   = root.querySelector('input[type="email"]');
  const btn     = root.querySelector('button[type="submit"]');
  const btnLabel= btn ? btn.querySelector('.btn-label') : null;
  const success = root.querySelector('.success');
  const micro   = root.querySelector('.micro');
  const errorEl = root.querySelector('.error');
  const counter = root.querySelector('.counter');
  const pos     = root.querySelector('.position-num');
  const eShown  = root.querySelector('.email-shown');
  const pcopy   = success ? success.querySelector('.pcopy') : null;

  /* referral DOM */
  const refBlock  = root.querySelector('.referral');
  const refLink   = root.querySelector('.ref-link');
  const refCopy   = root.querySelector('.ref-copy');
  const refCopyLb = root.querySelector('.ref-copy-label');
  const refBar    = root.querySelector('.ref-bar');
  const refCurEl  = root.querySelector('.ref-count-current');
  const refNeedEl = root.querySelector('.ref-next-needed');
  const refNextRw = root.querySelector('.ref-next-reward');
  const refTiers  = Array.from(root.querySelectorAll('.ref-tier'));
  const shareBtns = Array.from(root.querySelectorAll('.ref-share-btn'));

  const startingCount    = parseInt(root.dataset.startingCount || '0', 10);
  const cap              = parseInt(root.dataset.cap || '1000', 10);
  const backendUrl       = (root.dataset.backendUrl || '').replace(/\/+$/, '');
  const defaultCopy      = pcopy ? pcopy.textContent : '';
  const overCapCopy      = root.dataset.successCopyPostCap || '';
  const shareText        = root.dataset.shareText || 'Join me on the Prox waitlist:';
  const shareBaseRaw     = (root.dataset.shareBase || '').trim();
  const STORAGE = 'prox.waitlist.v2';
  const STORAGE_LEGACY = 'prox.waitlist.v1';

  const tiers = refTiers.map((el) => ({
    el,
    n: parseInt(el.dataset.tierCount || '0', 10),
    reward: (el.querySelector('.ref-tier-reward') || {}).textContent || '',
  })).filter(t => t.n > 0).sort((a, b) => a.n - b.n);

  /* ---------- referral attribution capture ----------------------------- */
  const REF_KEY = 'prox.waitlist.ref';
  function captureIncomingRef() {
    try {
      const url = new URL(window.location.href);
      const ref = url.searchParams.get('ref');
      if (ref && /^[A-Za-z0-9_-]{4,32}$/.test(ref)) {
        sessionStorage.setItem(REF_KEY, ref.toUpperCase());
      }
    } catch (_) {}
  }
  function getIncomingRef() {
    try { return sessionStorage.getItem(REF_KEY) || ''; } catch (_) { return ''; }
  }
  captureIncomingRef();

  /* ---------- marquee ---------------------------------------------------- */
  const track = root.querySelector('.marquee-track');
  if (track) {
    const items = ['Hybrid Energy', 'Hybrid Humans', 'Protein + Caffeine', '0g Sugar', 'Sparkling', '12 fl oz'];
    const starUrl = track.dataset.starUrl || '';
    const block = items.map(t =>
      `<span class="marquee-item">${t}<span class="star" aria-hidden="true" style="background-image:url('${starUrl}')"></span></span>`
    ).join('');
    track.innerHTML = block + block;
  }

  /* ---------- persistence ------------------------------------------------ */
  function loadStored() {
    try {
      const v2 = JSON.parse(localStorage.getItem(STORAGE) || 'null');
      if (v2) return v2;
      const v1 = JSON.parse(localStorage.getItem(STORAGE_LEGACY) || 'null');
      if (v1 && v1.email) return { ...v1, code: null, referralCount: 0 };
    } catch (_) {}
    return null;
  }
  function saveStored(data) {
    try { localStorage.setItem(STORAGE, JSON.stringify({ ...data, at: Date.now() })); } catch (_) {}
  }

  /* ---------- local code generator (fallback when no backend) ---------- */
  function generateLocalCode() {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no 0/O/1/I
    let out = 'PROX-';
    for (let i = 0; i < 6; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
    return out;
  }

  /* ---------- live counter ---------------------------------------------- */
  let base = startingCount;
  let stored = loadStored();
  if (startingCount > 0 && stored && stored.position) {
    base = Math.max(base, stored.position + Math.floor(Math.random() * 8));
  }
  let drift = base;
  if (counter) counter.textContent = drift.toLocaleString();

  if (startingCount > 0) {
    setInterval(() => {
      drift += Math.random() < 0.7 ? 1 : 0;
      if (counter) counter.textContent = drift.toLocaleString();
    }, 4000 + Math.random() * 3000);
  }

  /* ---------- referral rendering --------------------------------------- */
  function buildShareUrl(code) {
    let base;
    if (shareBaseRaw) {
      base = shareBaseRaw;
    } else {
      const u = new URL(window.location.href);
      u.searchParams.delete('ref');
      u.hash = '';
      base = u.toString();
    }
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}ref=${encodeURIComponent(code)}`;
  }

  function renderReferral(code, count) {
    if (!refBlock || !code) return;
    refBlock.hidden = false;

    const shareUrl = buildShareUrl(code);
    if (refLink) refLink.value = shareUrl;
    if (refCurEl) refCurEl.textContent = count.toLocaleString();

    /* tier states */
    let nextTier = null;
    tiers.forEach((t) => {
      const hit = count >= t.n;
      t.el.classList.toggle('is-hit', hit);
      if (!hit && !nextTier) nextTier = t;
    });

    /* progress + next-tier label */
    const topN = tiers.length ? tiers[tiers.length - 1].n : 0;
    const pct = topN ? Math.min(100, (count / topN) * 100) : 0;
    if (refBar) refBar.style.width = pct.toFixed(1) + '%';

    if (nextTier) {
      if (refNeedEl) refNeedEl.textContent = (nextTier.n - count).toString();
      if (refNextRw) refNextRw.textContent = (nextTier.reward || '').toLowerCase();
    } else {
      const wrap = root.querySelector('.ref-progress-label');
      if (wrap) wrap.innerHTML = `<span class="ref-count-current">${count.toLocaleString()}</span> referred · max tier unlocked`;
    }

    /* share intents */
    const msg = `${shareText} ${shareUrl}`;
    shareBtns.forEach((a) => {
      const kind = a.dataset.share;
      if (kind === 'sms')      a.href = `sms:?&body=${encodeURIComponent(msg)}`;
      if (kind === 'email')    a.href = `mailto:?subject=${encodeURIComponent('Get on the Prox waitlist')}&body=${encodeURIComponent(msg)}`;
      if (kind === 'twitter')  a.href = `https://x.com/intent/tweet?text=${encodeURIComponent(msg)}`;
      if (kind === 'whatsapp') a.href = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    });
  }

  function showSuccess(email, position, code, referralCount) {
    if (form)    form.style.display = 'none';
    if (micro)   micro.style.display = 'none';
    if (errorEl) errorEl.classList.remove('is-shown');
    if (success) {
      success.classList.add('is-shown');
      if (pos)    pos.textContent    = (position || 0).toLocaleString();
      if (eShown) eShown.textContent = email;
      if (pcopy)  pcopy.textContent  = (position > cap && overCapCopy) ? overCapCopy : defaultCopy;
    }
    if (code) renderReferral(code, referralCount || 0);
  }

  /* ---------- copy ------------------------------------------------------ */
  if (refCopy && refLink) {
    refCopy.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(refLink.value);
      } catch (_) {
        refLink.select();
        try { document.execCommand('copy'); } catch (_) {}
      }
      if (refCopyLb) {
        const prev = refCopyLb.textContent;
        refCopyLb.textContent = 'Copied';
        refCopy.classList.add('is-copied');
        setTimeout(() => {
          refCopyLb.textContent = prev;
          refCopy.classList.remove('is-copied');
        }, 1600);
      }
    });
  }

  /* ---------- error ----------------------------------------------------- */
  function showError(msg) {
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.classList.add('is-shown');
    setTimeout(() => errorEl.classList.remove('is-shown'), 4000);
  }

  function isEmail(s) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  }

  /* ---------- backend calls -------------------------------------------- */
  async function joinBackend(email, ref) {
    if (!backendUrl) return null;
    try {
      const resp = await fetch(backendUrl + '/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, ref: ref || undefined }),
      });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const data = await resp.json();
      if (typeof data.position !== 'number') throw new Error('no position');
      return data;
    } catch (err) {
      console.warn('[waitlist] backend /join failed', err);
      return null;
    }
  }

  async function refreshMe(code) {
    if (!backendUrl || !code) return null;
    try {
      const resp = await fetch(backendUrl + '/me?code=' + encodeURIComponent(code));
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      return await resp.json();
    } catch (err) {
      console.warn('[waitlist] /me failed', err);
      return null;
    }
  }

  /* ---------- restore prior signup ------------------------------------- */
  if (stored && stored.email) {
    showSuccess(stored.email, stored.position, stored.code, stored.referralCount || 0);
    /* If backend is wired and we have a code, refresh the count now + poll. */
    if (backendUrl && stored.code) {
      refreshMe(stored.code).then((me) => {
        if (!me) return;
        const updated = { ...stored, referralCount: me.referralCount || 0, position: me.position || stored.position };
        saveStored(updated);
        renderReferral(stored.code, updated.referralCount);
        if (pos && updated.position) pos.textContent = updated.position.toLocaleString();
      });
    }
  }

  /* poll while page is open so referral count updates live */
  if (backendUrl) {
    setInterval(async () => {
      if (document.hidden) return;
      const cur = loadStored();
      if (!cur || !cur.code) return;
      const me = await refreshMe(cur.code);
      if (!me) return;
      const updated = { ...cur, referralCount: me.referralCount || 0, position: me.position || cur.position };
      saveStored(updated);
      renderReferral(cur.code, updated.referralCount);
      if (pos && updated.position) pos.textContent = updated.position.toLocaleString();
    }, 30000);
  }

  if (!form) return;

  /* ---------- submit --------------------------------------------------- */
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = (input.value || '').trim();
    if (!isEmail(email)) {
      input.focus();
      showError('Enter a valid email');
      return;
    }
    btn.disabled = true;
    if (btnLabel) btnLabel.textContent = 'Adding';

    const ref = getIncomingRef();

    /* Path A: backend wired — /join handles everything. */
    let backendResp = null;
    if (backendUrl) {
      backendResp = await joinBackend(email, ref);
    }

    /* Path B: no backend — fall back to Shopify contact form for the customer
       record, generate the referral code locally (no real attribution). */
    if (!backendResp) {
      const fd = new FormData();
      fd.append('form_type', 'customer');
      fd.append('utf8', '✓');
      fd.append('contact[email]', email);
      fd.append('contact[tags]', ref ? `waitlist,referred-by:${ref}` : 'waitlist');
      try {
        const resp = await fetch('/contact#contact_form', {
          method: 'POST',
          body: fd,
          headers: { 'Accept': 'text/html' },
          credentials: 'same-origin',
        });
        if (!resp.ok && resp.status !== 302) throw new Error('HTTP ' + resp.status);
      } catch (err) {
        console.warn('[waitlist] contact form submission warning', err);
      }
    }

    const position = backendResp?.position ?? (drift + 1);
    const code     = backendResp?.code     ?? generateLocalCode();
    const refCount = backendResp?.referralCount ?? 0;

    drift = position;
    if (counter) counter.textContent = drift.toLocaleString();
    saveStored({ email, position, code, referralCount: refCount });

    /* clear the captured ref so it doesn't carry across signups in the same tab */
    try { sessionStorage.removeItem(REF_KEY); } catch (_) {}

    showSuccess(email, position, code, refCount);
  });
})();
